import React, { createContext, useContext, ReactNode } from 'react';
import { UserInfo, UserPermissions } from '../api/client';

interface PermissionContextValue {
  userInfo: UserInfo | null;
  hasPermission: (path: string) => boolean;
  role: 'user' | 'admin' | null;
  roleLabel: string;
}

const defaultPermissions: UserPermissions = {
  submissions: { view: false, moderate: false, delete: false, delete_permanent: false },
  ip: { view: false, manage: false },
  forms: { view: false, create: false, edit: false, delete: false },
  channels: { view: false, manage: false },
  integrations: { view: false, manage: false },
  analytics: { view: false },
  costs: { view: false },
  api_keys: { view: false, manage: false },
  settings: { view: false, manage: false },
  reputation: { view: false, manage: false },
};

const PermissionContext = createContext<PermissionContextValue>({
  userInfo: null,
  hasPermission: () => false,
  role: null,
  roleLabel: '',
});

export const usePermissions = () => useContext(PermissionContext);

interface PermissionProviderProps {
  userInfo: UserInfo | null;
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ userInfo, children }) => {
  const hasPermission = (path: string): boolean => {
    if (!userInfo?.permissions) return false;

    const parts = path.split('.');
    if (parts.length !== 2) return false;

    const [category, action] = parts;
    const categoryPerms = userInfo.permissions[category as keyof UserPermissions];

    if (!categoryPerms) return false;

    return (categoryPerms as Record<string, boolean>)[action] ?? false;
  };

  const value: PermissionContextValue = {
    userInfo,
    hasPermission,
    role: userInfo?.role ?? null,
    roleLabel: userInfo?.role_label ?? '',
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// PermissionGate component for conditional rendering
interface PermissionGateProps {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  children,
  fallback = null,
}) => {
  const { hasPermission } = usePermissions();

  if (hasPermission(permission)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

// Hook for checking multiple permissions
export const useHasPermission = (permission: string): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
};

// Hook for checking any of multiple permissions
export const useHasAnyPermission = (permissions: string[]): boolean => {
  const { hasPermission } = usePermissions();
  return permissions.some(p => hasPermission(p));
};

// Hook for checking all of multiple permissions
export const useHasAllPermissions = (permissions: string[]): boolean => {
  const { hasPermission } = usePermissions();
  return permissions.every(p => hasPermission(p));
};

export default PermissionContext;
